/**
 * Theme settings — the "Theme" tab of the storefront appearance editor
 * (module 22 tasks C2/E1, the wireframe's left panel).
 *
 * Curated controls only: a preset picker, seven brand colours, a corner radius
 * and a font pairing. There is deliberately no CSS field — the module's
 * baked-in decision is "override the token, never restyle a component". Every
 * change flows up through `onChange`; the page holds the theme, stages it with
 * the sections, and the preview re-skins live.
 */
import {
  STORE_FONT_PAIRINGS,
  STORE_THEME_PRESETS,
  storeFontPairing,
  type StoreFontPairing,
  type StoreTheme,
} from "@flint/api-contract/store-home";
import { type ReactNode } from "react";
import { Input, Textarea } from "@flint/ui";
import { SectionImageField } from "./section-image-field.tsx";

const COLOUR_FIELDS: ReadonlyArray<{ key: keyof StoreTheme; label: string; hint?: string }> = [
  { key: "accent", label: "Accent / brand", hint: "Buttons, links, active state" },
  { key: "accentFg", label: "Accent text", hint: "Text on accent-filled buttons" },
  { key: "bg", label: "Page background" },
  { key: "surface", label: "Cards & header" },
  { key: "ink", label: "Text" },
  { key: "muted", label: "Muted text" },
  { key: "line", label: "Borders" },
];

const FONT_LABELS: Record<StoreFontPairing, string> = {
  spark: STORE_FONT_PAIRINGS.spark.label,
  modern: STORE_FONT_PAIRINGS.modern.label,
  classic: STORE_FONT_PAIRINGS.classic.label,
  bold: STORE_FONT_PAIRINGS.bold.label,
};

/** A labelled control row with an optional char counter and hint. */
function LabelledField({
  label,
  htmlFor,
  hint,
  count,
  max,
  children,
}: {
  label: string;
  htmlFor: string;
  hint?: string;
  count?: number;
  max?: number;
  children: ReactNode;
}) {
  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between gap-2">
        <label className="block text-sm font-medium text-ink" htmlFor={htmlFor}>
          {label}
        </label>
        {typeof count === "number" && typeof max === "number" ? (
          <span className={`font-mono text-xs ${count > max ? "text-bad" : "text-muted"}`}>
            {count}/{max}
          </span>
        ) : null}
      </div>
      {children}
      {hint ? <p className="mt-1 text-xs text-muted">{hint}</p> : null}
    </div>
  );
}

export function ThemePanel({
  theme,
  canEdit,
  onChange,
  storeName,
}: {
  theme: StoreTheme;
  canEdit: boolean;
  onChange: (next: StoreTheme) => void;
  /** The store's name — drives the SEO preview's derived title fallback. */
  storeName: string;
}) {
  const set = <K extends keyof StoreTheme>(key: K, value: StoreTheme[K]) =>
    onChange({ ...theme, [key]: value });

  // Mirror the storefront's head() so the preview shows the buyer-visible
  // result: seller-set fields win, otherwise derive from the store name.
  const previewSuffix = theme.titleSuffix ?? " — wholesale store";
  const previewTitle = theme.metaTitle?.trim()
    ? theme.metaTitle
    : `${storeName}${previewSuffix}`;
  const previewDescription =
    theme.metaDescription?.trim() || "Your store's tagline or description appears here.";

  return (
    <div className="flex flex-col gap-5 p-4">
      {/* ── Presets ─────────────────────────────────────────── */}
      <section>
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">
          Theme presets
        </h3>
        <div className="grid grid-cols-2 gap-2">
          {Object.entries(STORE_THEME_PRESETS).map(([id, preset]) => {
            const active = theme.preset === id;
            return (
              <button
                key={id}
                type="button"
                disabled={!canEdit}
                onClick={() => onChange({ ...preset })}
                className={`rounded-md border p-2 text-left transition disabled:opacity-50 ${
                  active ? "border-sky-600 ring-2 ring-sky-100" : "border-line hover:border-sky-600"
                }`}
              >
                <span className="mb-1.5 flex gap-1">
                  {[preset.accent, preset.bg, preset.ink].map((c, i) => (
                    <span
                      key={i}
                      className="h-4 w-4 rounded-sm border border-line"
                      style={{ backgroundColor: c }}
                    />
                  ))}
                </span>
                <span className="block text-xs font-semibold capitalize text-ink">{id}</span>
              </button>
            );
          })}
        </div>
      </section>

      {/* ── Colours ─────────────────────────────────────────── */}
      <section>
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">Colours</h3>
        <div className="flex flex-col gap-2.5">
          {COLOUR_FIELDS.map((f) => (
            <div key={f.key} className="flex items-center gap-3">
              <label className="relative h-9 w-9 shrink-0 overflow-hidden rounded-md border border-line">
                <input
                  type="color"
                  disabled={!canEdit}
                  aria-label={f.label}
                  value={String(theme[f.key])}
                  onChange={(e) => set(f.key, e.target.value as never)}
                  className="absolute -inset-1 h-[calc(100%+8px)] w-[calc(100%+8px)] cursor-pointer border-0 p-0"
                />
              </label>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-ink">{f.label}</div>
                {f.hint ? <div className="truncate text-xs text-muted">{f.hint}</div> : null}
              </div>
              <input
                type="text"
                disabled={!canEdit}
                value={String(theme[f.key])}
                onChange={(e) => {
                  const v = e.target.value.trim();
                  if (/^#[0-9a-fA-F]{6}$/.test(v)) set(f.key, v as never);
                }}
                className="w-24 rounded-sm border border-line px-2 py-1 font-mono text-xs uppercase text-ink"
              />
            </div>
          ))}
        </div>
      </section>

      {/* ── Shape ───────────────────────────────────────────── */}
      <section>
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">
          Shape &amp; type
        </h3>
        <label className="mb-1 block text-sm font-medium text-ink" htmlFor="theme-radius">
          Corner radius
        </label>
        <div className="mb-4 flex items-center gap-3">
          <input
            id="theme-radius"
            type="range"
            min={0}
            max={24}
            step={1}
            disabled={!canEdit}
            value={theme.radius}
            onChange={(e) => set("radius", Number(e.target.value))}
            className="flex-1 accent-sky-600"
          />
          <span className="w-10 text-right font-mono text-xs text-muted">{theme.radius}px</span>
        </div>

        <label className="mb-1 block text-sm font-medium text-ink" htmlFor="theme-font">
          Font pairing
        </label>
        <select
          id="theme-font"
          disabled={!canEdit}
          value={theme.fontPairing}
          onChange={(e) => set("fontPairing", storeFontPairing.parse(e.target.value))}
          className="mb-4 w-full rounded-sm border border-line bg-paper px-2 py-2 text-sm text-ink"
        >
          {storeFontPairing.options.map((opt) => (
            <option key={opt} value={opt}>
              {FONT_LABELS[opt]}
            </option>
          ))}
        </select>

        <label className="mb-1 block text-sm font-medium text-ink" htmlFor="theme-base">
          Base text size
        </label>
        <div className="flex items-center gap-3">
          <input
            id="theme-base"
            type="range"
            min={13}
            max={20}
            step={1}
            disabled={!canEdit}
            value={theme.baseSize ?? 16}
            onChange={(e) => set("baseSize", Number(e.target.value))}
            className="flex-1 accent-sky-600"
          />
          <span className="w-10 text-right font-mono text-xs text-muted">
            {theme.baseSize ?? 16}px
          </span>
        </div>
      </section>

      {/* ── Layout ──────────────────────────────────────────── */}
      <section>
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">Layout</h3>
        <label className="mb-1 block text-sm font-medium text-ink" htmlFor="theme-container">
          Content width
        </label>
        <div className="mb-4 flex items-center gap-3">
          <input
            id="theme-container"
            type="range"
            min={960}
            max={1440}
            step={20}
            disabled={!canEdit}
            value={theme.containerWidth ?? 1120}
            onChange={(e) => set("containerWidth", Number(e.target.value))}
            className="flex-1 accent-sky-600"
          />
          <span className="w-14 text-right font-mono text-xs text-muted">
            {theme.containerWidth ?? 1120}px
          </span>
        </div>

        <label className="mb-1 block text-sm font-medium text-ink" htmlFor="theme-spacing">
          Section spacing
        </label>
        <div className="flex items-center gap-3">
          <input
            id="theme-spacing"
            type="range"
            min={16}
            max={96}
            step={4}
            disabled={!canEdit}
            value={theme.sectionSpacing ?? 48}
            onChange={(e) => set("sectionSpacing", Number(e.target.value))}
            className="flex-1 accent-sky-600"
          />
          <span className="w-10 text-right font-mono text-xs text-muted">
            {theme.sectionSpacing ?? 48}px
          </span>
        </div>
      </section>

      {/* ── Branding ────────────────────────────────────────── */}
      <section>
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">Branding</h3>
        {canEdit ? (
          <div className="flex flex-col gap-4">
            <SectionImageField
              label="Store logo"
              value={theme.logoUrl}
              onChange={(url) => set("logoUrl", url)}
              hint="Replaces the wordmark in your storefront header. PNG or WebP, up to 5 MB. Publishes with the theme."
            />
            {theme.logoUrl ? (
              <div>
                <label className="mb-1 block text-sm font-medium text-ink" htmlFor="theme-logo-h">
                  Logo height
                </label>
                <div className="flex items-center gap-3">
                  <input
                    id="theme-logo-h"
                    type="range"
                    min={20}
                    max={72}
                    step={2}
                    disabled={!canEdit}
                    value={theme.logoHeight ?? 28}
                    onChange={(e) => set("logoHeight", Number(e.target.value))}
                    className="flex-1 accent-sky-600"
                  />
                  <span className="w-10 text-right font-mono text-xs text-muted">
                    {theme.logoHeight ?? 28}px
                  </span>
                </div>
              </div>
            ) : null}
            <SectionImageField
              label="Favicon"
              value={theme.faviconUrl}
              onChange={(url) => set("faviconUrl", url)}
              hint="The little icon in the browser tab. A square PNG works best."
            />
          </div>
        ) : theme.logoUrl ? (
          <img src={theme.logoUrl} alt="Store logo" className="h-10 object-contain" />
        ) : (
          <p className="text-xs text-muted">No logo uploaded.</p>
        )}
      </section>

      {/* ── SEO & sharing ───────────────────────────────────── */}
      <section>
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">
          SEO &amp; sharing
        </h3>
        <p className="mb-3 text-xs text-muted">
          How your storefront looks in Google results and when its link is shared. Leave a field
          blank to use the sensible default shown below.
        </p>

        {/* Live search / social preview — mirrors the storefront head(). */}
        <div className="mb-4 rounded-md border border-line bg-bg p-3">
          <div className="truncate text-[0.8125rem] text-trust-text">Your storefront link</div>
          <div className="truncate text-[0.9375rem] font-medium text-info">{previewTitle}</div>
          <div className="mt-0.5 line-clamp-2 text-xs text-muted">{previewDescription}</div>
        </div>

        {canEdit ? (
          <div className="flex flex-col gap-4">
            <LabelledField
              label="Meta title"
              htmlFor="seo-title"
              count={theme.metaTitle?.length ?? 0}
              max={70}
            >
              <Input
                id="seo-title"
                value={theme.metaTitle ?? ""}
                maxLength={70}
                placeholder={`${storeName}${previewSuffix}`}
                onChange={(e) => set("metaTitle", e.target.value)}
              />
            </LabelledField>

            <LabelledField
              label="Browser tab suffix"
              htmlFor="seo-suffix"
              hint="Appended after your store name when Meta title is blank. Clear it for just the name."
            >
              <Input
                id="seo-suffix"
                value={theme.titleSuffix ?? " — wholesale store"}
                maxLength={48}
                placeholder=" — wholesale store"
                onChange={(e) => set("titleSuffix", e.target.value)}
              />
            </LabelledField>

            <LabelledField
              label="Meta description"
              htmlFor="seo-desc"
              count={theme.metaDescription?.length ?? 0}
              max={200}
            >
              <Textarea
                id="seo-desc"
                value={theme.metaDescription ?? ""}
                maxLength={200}
                rows={3}
                placeholder="One or two lines describing what you sell — shown in search results and link previews."
                onChange={(e) => set("metaDescription", e.target.value)}
              />
            </LabelledField>

            <SectionImageField
              label="Social share image"
              value={theme.socialImageUrl}
              onChange={(url) => set("socialImageUrl", url)}
              hint="Shown when your storefront link is shared (WhatsApp, social). Landscape ~1200×630 works best; falls back to your hero or cover image."
            />
          </div>
        ) : (
          <p className="text-xs text-muted">Ask an admin for edit access to change SEO settings.</p>
        )}
      </section>

      {!canEdit ? (
        <p className="text-xs text-muted">You can view this theme. Ask an admin for edit access.</p>
      ) : null}
    </div>
  );
}
